import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  FileText, 
  Calendar, 
  MapPin, 
  TrendingUp, 
  CheckCircle2,
  ArrowLeft,
  Download,
  Share2,
  Info,
  Zap,
  Building,
  Loader2,
  Copy,
  Check,
  AlertCircle,
  User,
  Save
} from "lucide-react";
import Navigation from "@/components/Navigation";
import PublicNavigation from "@/components/PublicNavigation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContactModal } from "@/components/ContactModal";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import { generateTicfePDF } from "@/utils/pdfExport/ticfe";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export const ReportViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isPublicView, setIsPublicView] = useState(true); // Mode vue publique par défaut

  // Contact modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    title: ''
  });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    loadReport();
    
    // Nettoyer les flags de navigation s'ils existent
    const fromUpload = sessionStorage.getItem('from_upload') === 'true';
    if (fromUpload) {
      sessionStorage.removeItem('from_upload');
    }
  }, [id]);

  // Determine view mode based on authentication status
  // If user is authenticated, show admin controls; otherwise show public view
  useEffect(() => {
    if (!authLoading) {
      setIsPublicView(!user);
    }
  }, [user, authLoading]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reports/${id}`);
      const reportData = response.data.report;
      setReport(reportData);
      setError(null);
      
      // Load existing contact info
      if (reportData.contact) {
        setContactInfo({
          name: reportData.contact.name || '',
          phone: reportData.contact.phone || '',
          email: reportData.contact.email || '',
          address: reportData.contact.office_address || '',
          title: reportData.contact.title || ''
        });
      }
    } catch (err) {
      console.error('Error loading report:', err);
      setError('Rapport non trouvé');
      toast.error('Erreur', {
        description: 'Impossible de charger le rapport.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const response = await axios.put(
        `${API}/reports/${id}/contact-info`,
        contactInfo,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.data.success) {
        // Update local report state with new contact
        setReport(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            name: contactInfo.name,
            phone: contactInfo.phone,
            email: contactInfo.email,
            office_address: contactInfo.address,
            title: contactInfo.title
          }
        }));
        setShowContactModal(false);
        toast.success('Contact mis à jour', {
          description: 'Les coordonnées seront utilisées dans le PDF.'
        });
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      toast.error('Erreur lors de la sauvegarde du contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleExport = async () => {
    if (!report) return;
    try {
      await generateTicfePDF(report);
      toast.success('PDF exporté', { description: 'Le rapport a été téléchargé.' });
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error("Erreur lors de la génération du PDF");
    }
  };
  
  const handleShare = async () => {
    const baseUrl = window.location.origin;
    const shareLink = `${baseUrl}/report/${report.share_token}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        toast.success('Lien copié', {
          description: 'Le lien du rapport a été copié dans le presse-papier.',
        });
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (clipboardError) {
      // Silently fall through to fallback method
    }
    
    // Fallback: Try execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success('Lien copié', {
          description: 'Le lien du rapport a été copié dans le presse-papier.',
        });
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('execCommand failed');
      }
    } catch (fallbackError) {
      // Last resort: show the link for manual copying
      toast.info('Lien du rapport', {
        description: shareLink,
        duration: 10000,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Chargement du rapport...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center mt-20">
            <Card>
              <CardContent className="p-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Rapport non trouvé</h2>
                <p className="text-muted-foreground mb-6">
                  Ce rapport n'existe pas ou a été supprimé.
                </p>
                <Button onClick={() => navigate('/reports')}>
                  Retour aux rapports
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const uploadDate = new Date(report.upload_date);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
      {isPublicView ? <PublicNavigation showHomeLink={!!user} /> : <Navigation />}
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            {!isPublicView && (
              <Link to="/reports">
                <Button variant="ghost" size="sm" className="mb-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour aux rapports
                </Button>
              </Link>
            )}
            
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                    <FileText className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold font-display">
                      {report.client_name}
                    </h1>
                    <p className="text-muted-foreground">
                      Rapport d'éligibilité TICFE
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {format(uploadDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    PDL: {report.pdl}
                  </div>
                  {report.address?.city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {report.address.city}
                    </div>
                  )}
                </div>
              </div>
              
              {!isPublicView && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" onClick={() => setShowContactModal(true)} data-testid="contact-pdf-btn">
                    <User className="w-4 h-4 mr-2" />
                    Contact PDF
                  </Button>
                  <Button variant="outline" onClick={handleShare}>
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                    {copied ? 'Copié !' : 'Partager'}
                  </Button>
                  <Button onClick={handleExport}>
                    <Download className="w-4 h-4 mr-2" />
                    Exporter PDF
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Eligibility Status */}
          {report.eligibility && report.eligibility === 'Eligible' ? (
            <Alert className="mb-8 bg-success/10 border-success/20">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <AlertTitle className="text-success font-semibold">Éligible au Tarif Réduit</AlertTitle>
              <AlertDescription>
                {report.eligibility_reason || 'Votre entreprise est éligible au tarif réduit de TICFE.'}
              </AlertDescription>
            </Alert>
          ) : report.eligibility && report.eligibility === 'Non-Eligible' ? (
            <Alert className="mb-8 bg-destructive/10 border-destructive/20">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <AlertTitle className="text-destructive font-semibold">Non Éligible au Tarif Réduit</AlertTitle>
              <AlertDescription>
                {report.eligibility_reason || 'Votre entreprise n\'est pas éligible au tarif réduit de TICFE selon les critères actuels.'}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="mb-8 bg-success/10 border-success/20">
              <CheckCircle2 className="w-5 h-5 text-success" />
              <AlertTitle className="text-success font-semibold">Éligible au Tarif Réduit</AlertTitle>
              <AlertDescription>
                Votre entreprise est éligible au tarif réduit de TICFE.
              </AlertDescription>
            </Alert>
          )}

          {/* Share Link Display - Only for admin */}
          {!isPublicView && (
            <Card className="mb-8 bg-accent/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Share2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium mb-1">Lien de partage</p>
                    <code className="text-xs text-muted-foreground block truncate">
                      {window.location.href}
                    </code>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleShare}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Content - Same as before but with report data */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
              <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
              <TabsTrigger value="savings">Économies</TabsTrigger>
              <TabsTrigger value="details">Détails</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Savings Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SavingsCard
                  title="Attestation (12 mois)"
                  amount={report.savings.attestation}
                  description="Économies sur les 12 prochains mois"
                  icon={<TrendingUp className="w-5 h-5" />}
                />
                {Object.entries(report.savings?.reimbursement || {})
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, amount]) => {
                    const year = key.replace('year_', '');
                    return (
                      <SavingsCard
                        key={key}
                        title={`Remboursement ${year}`}
                        amount={amount}
                        description={`Économies potentielles pour ${year}`}
                        icon={<TrendingUp className="w-5 h-5" />}
                      />
                    );
                  })}
              </div>

              {/* TICFE Explanation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Qu'est-ce que la TICFE ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    La <strong>TICFE</strong> (Taxe Intérieure sur la Consommation Finale d'Électricité), 
                    également appelée <strong>Accise sur l'Énergie</strong> ou <strong>CSPE</strong>, est une taxe applicable 
                    à la consommation d'électricité de tous les consommateurs.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Elle est gérée, collectée et contrôlée par l'administration fiscale depuis 2022. 
                    Les fournisseurs d'électricité collectent et reversent les montants à l'État.
                  </p>
                  <div className="bg-accent/50 rounded-lg p-4 mt-4">
                    <p className="text-sm leading-relaxed">
                      💡 <strong>Bon à savoir :</strong> Selon votre activité, vous pouvez bénéficier d'un tarif réduit 
                      qui peut représenter des économies significatives sur votre facture d'électricité.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Eligibility Explanation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-success" />
                    Qui est éligible au tarif réduit ?
                  </CardTitle>
                  <CardDescription>
                    Les catégories d'entreprises pouvant bénéficier d'un tarif réduit de TICFE
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-accent/30 rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Entreprises industrielles électro-intensives</h4>
                        <p className="text-sm text-muted-foreground">
                          Entreprises dont l'électricité représente une part importante des coûts de production
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/30 rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Entreprises exposées au risque de fuite de carbone</h4>
                        <p className="text-sm text-muted-foreground">
                          Entreprises dont l'activité est exposée à un risque important de fuite de carbone
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/30 rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Entreprises hyper électro-intensives</h4>
                        <p className="text-sm text-muted-foreground">
                          Entreprises pour lesquelles le coût de l'électricité représente au moins 50% du coût de production d'un produit
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tariffs Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Tarifs applicables en 2026</CardTitle>
                  <CardDescription>
                    Tarifs de l'accise sur l'électricité selon les catégories fiscales (en €/MWh)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold">Catégorie fiscale</th>
                          <th className="text-right py-3 px-4 font-semibold">Janvier</th>
                          <th className="text-right py-3 px-4 font-semibold">À partir du 1er février</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="py-3 px-4">Ménages et assimilés</td>
                          <td className="text-right py-3 px-4">29,98 €</td>
                          <td className="text-right py-3 px-4">30,85 €</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-3 px-4">PME (36-250 kVA)</td>
                          <td className="text-right py-3 px-4">25,79 €</td>
                          <td className="text-right py-3 px-4">26,58 €</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 font-medium">Haute puissance (&gt;250 kVA)</td>
                          <td className="text-right py-3 px-4 font-medium text-primary">25,79 €</td>
                          <td className="text-right py-3 px-4 font-medium text-primary">26,58 €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <Alert className="mt-4">
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                      Tarifs officiels 2026 (source : EDF/ENGIE). Hausse au 1er février liée à l'indexation et la fin du bouclier tarifaire.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="savings" className="space-y-6">
              {/* Introduction */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Les 2 méthodes de récupération de la TICFE
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Il existe deux systèmes pour bénéficier du tarif réduit de TICFE. Chaque système a ses avantages 
                    et peut être utilisé selon votre situation. Vous pouvez même combiner les deux pour maximiser vos économies.
                  </p>
                </CardContent>
              </Card>

              {/* Attestation System */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    1. Le Système de l'Attestation
                  </CardTitle>
                  <CardDescription>
                    Permet d'éviter de payer les tarifs pleins sur vos futures factures
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Explication détaillée */}
                  <div className="prose prose-sm max-w-none">
                    <p className="text-muted-foreground leading-relaxed">
                      Le système de l'attestation permet à votre entreprise de <strong>ne pas payer le tarif plein de TICFE 
                      dès maintenant</strong>. Vous déclarez votre éligibilité à votre fournisseur d'électricité qui applique 
                      directement le tarif réduit sur vos factures futures.
                    </p>
                  </div>

                  <div className="bg-accent/50 rounded-xl p-8 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Économies potentielles</p>
                    <p className="text-5xl font-bold text-primary mb-2">
                      {report.savings.attestation.toLocaleString('fr-FR')} €
                    </p>
                    <p className="text-sm text-muted-foreground">Sur les 12 prochains mois</p>
                  </div>

                  {/* Avantages */}
                  <div className="bg-success/10 rounded-lg p-4 border border-success/20">
                    <h4 className="font-semibold text-success mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Avantages
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Économies immédiates sur vos factures futures</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Pas d'avance de trésorerie nécessaire</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Valable pour toute la durée du contrat</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Rappels et obligations */}
                  <Alert>
                    <AlertCircle className="w-4 h-4" />
                    <AlertTitle>⚠️ Important - Obligations à respecter</AlertTitle>
                    <AlertDescription className="space-y-2 mt-2">
                      <p>
                        <strong>Audit énergétique obligatoire :</strong> Vous devez réaliser un audit énergétique 
                        dans les <strong>6 mois suivant la clôture de votre exercice fiscal</strong>.
                      </p>
                      <p>
                        <strong>Vérification préalable :</strong> Cette déclaration nécessite une vérification préalable 
                        pour éviter tout risque de sanction fiscale. Place des Énergies vous accompagne dans cette démarche.
                      </p>
                      <p>
                        <strong>Contrôle annuel :</strong> Il est recommandé de vérifier votre éligibilité chaque année 
                        car les critères peuvent évoluer.
                      </p>
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Reimbursement System */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    2. Le Système du Remboursement
                  </CardTitle>
                  <CardDescription>
                    Récupérez la TICFE déjà payée sur les années antérieures
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Explication détaillée */}
                  <div className="prose prose-sm max-w-none">
                    <p className="text-muted-foreground leading-relaxed">
                      Le système de remboursement vous permet de <strong>récupérer rétroactivement la TICFE</strong> que vous 
                      avez déjà payée au tarif plein sur les années passées. C'est une méthode sûre, toutefois{' '}
                      <strong>en cas d'erreur dans le dossier, un redressement fiscal reste possible</strong>. 
                      Il est donc essentiel de constituer un dossier rigoureux et conforme.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {Object.entries(report.savings?.reimbursement || {})
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, amount]) => {
                        const year = key.replace('year_', '');
                        return (
                          <div key={key} className="bg-accent/50 rounded-xl p-6">
                            <p className="text-sm text-muted-foreground mb-2">Année {year}</p>
                            <p className="text-3xl font-bold text-primary">
                              {(amount || 0).toLocaleString('fr-FR')} €
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">Remboursement possible</p>
                          </div>
                        );
                      })}
                  </div>

                  {/* Avantages */}
                  <div className="bg-success/10 rounded-lg p-4 border border-success/20">
                    <h4 className="font-semibold text-success mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Avantages
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span><strong>Pas de sanction en cas d'erreur de bonne foi</strong> - Toutefois, l'administration fiscale peut demander des justificatifs complémentaires ou des précisions sur la méthodologie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Délai généreux : 2 ans + l'année en cours pour déposer votre demande</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Récupération d'argent déjà payé</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">✓</span>
                        <span>Pas d'audit énergétique obligatoire</span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Rappels */}
                  <Alert>
                    <Info className="w-4 h-4" />
                    <AlertTitle>📋 Documents nécessaires</AlertTitle>
                    <AlertDescription className="space-y-2 mt-2">
                      <p>
                        Pour constituer votre demande de remboursement, vous aurez besoin de :
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Vos factures d'électricité de la période concernée</li>
                        <li><strong>Le CERFA dûment complété par votre comptable</strong> (pour sécuriser la demande)</li>
                        <li>La preuve de votre éligibilité (code APE, données de consommation, etc.)</li>
                      </ul>
                      <p className="mt-2">
                        <strong>Place des Énergies vous accompagne</strong> dans la constitution de votre dossier pour 
                        maximiser vos chances d'acceptation. Un CERFA rempli par le comptable permet de sécuriser la demande de récupération.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {/* Avertissement important */}
                  <Alert variant="warning" className="border-yellow-500/50 bg-yellow-50/50">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <AlertTitle className="text-yellow-800">⚠️ Important - Vérification administrative</AlertTitle>
                    <AlertDescription className="text-sm text-yellow-900/80">
                      <p className="mb-2">
                        Bien qu'il n'y ait <strong>pas de sanction en cas d'erreur de bonne foi</strong>, l'administration fiscale peut :
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Demander des pièces justificatives complémentaires</li>
                        <li>Solliciter des précisions sur la méthodologie de calcul utilisée</li>
                        <li>Vérifier la cohérence des données avec vos déclarations fiscales</li>
                        <li>Procéder à un contrôle en cas de doute sur l'éligibilité</li>
                      </ul>
                      <p className="mt-2 font-medium">
                        💡 Recommandation : Conservez tous vos justificatifs (factures, calculs, attestations) pendant au moins 3 ans.
                      </p>
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Comparaison et recommandation */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Quelle méthode choisir ?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-muted-foreground leading-relaxed">
                      <strong>Bonne nouvelle :</strong> Vous pouvez combiner les deux systèmes ! 
                    </p>
                    <div className="bg-accent/50 rounded-lg p-4">
                      <p className="text-sm leading-relaxed">
                        💡 <strong>Notre recommandation :</strong> Commencez par demander le remboursement des années passées 
                        ({Object.keys(report.savings?.reimbursement || {}).map(k => k.replace('year_', '')).sort().join(', ')}) pour récupérer l'argent déjà payé. En parallèle, 
                        mettez en place le système d'attestation pour bénéficier du tarif réduit sur vos futures factures.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="details" className="space-y-6">
              {/* Conclusion Card */}
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <CheckCircle2 className="w-6 h-6 text-success" />
                    Conclusion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-base leading-relaxed">
                    {report.eligibility === 'Eligible' ? (
                      <span>
                        Suite à l'analyse de votre dossier, <strong>{report.client_name}</strong> est{' '}
                        <strong className="text-success">éligible au tarif réduit de TICFE</strong>. 
                        Cette éligibilité vous permet de réaliser des économies substantielles sur vos factures d'électricité.
                      </span>
                    ) : report.eligibility === 'Non-Eligible' ? (
                      <span>
                        Suite à l'analyse de votre dossier, <strong>{report.client_name}</strong> est{' '}
                        <strong className="text-destructive">non éligible au tarif réduit de TICFE</strong>.
                        {report.eligibility_reason && (
                          <span> Motif : {report.eligibility_reason}. </span>
                        )}
                        Cependant, nous restons à votre disposition pour analyser d'autres opportunités d'optimisation énergétique.
                      </span>
                    ) : (
                      <span>
                        Suite à l'analyse de votre dossier, <strong>{report.client_name}</strong> est{' '}
                        <strong className="text-success">éligible au tarif réduit de TICFE</strong>. 
                        Cette éligibilité vous permet de réaliser des économies substantielles sur vos factures d'électricité.
                      </span>
                    )}
                  </div>
                  
                  {report.eligibility === 'Eligible' || !report.eligibility ? (
                    <>
                      <div className="bg-accent/50 rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Zap className="w-5 h-5 text-primary" />
                          Comment Place des Énergies vous accompagne
                        </h4>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>Assistance complète pour les demandes de tarif réduit</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>Assistance à la constitution des dossiers de remboursement</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>Mise en place du tarif réduit sur vos factures fournisseur (système d'attestation)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>Coordination avec votre expert-comptable</span>
                          </li>
                        </ul>
                      </div>

                      <Alert>
                        <Info className="w-4 h-4" />
                        <AlertTitle>Notre engagement</AlertTitle>
                        <AlertDescription>
                          Place des Énergies facture une commission sur les économies réalisées uniquement. 
                          <strong> Si vous ne réalisez aucune économie, notre prestation est gratuite.</strong> Vous pouvez 
                          choisir d'utiliser un ou les deux systèmes de récupération pour maximiser vos économies.
                        </AlertDescription>
                      </Alert>
                    </>
                  ) : (
                    <Alert>
                      <Info className="w-4 h-4" />
                      <AlertTitle>Nos services</AlertTitle>
                      <AlertDescription>
                        Bien que votre entreprise ne soit pas éligible au tarif réduit de TICFE actuellement, 
                        Place des Énergies peut vous accompagner dans d'autres optimisations énergétiques et 
                        surveiller les évolutions de votre éligibilité. N'hésitez pas à nous contacter pour en savoir plus.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Client Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Informations du site</CardTitle>
                  <CardDescription>
                    Détails de votre installation et de votre activité
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <DetailRow label="Client" value={report.client_name} />
                  <Separator />
                  <DetailRow label="Point de Livraison (PDL)" value={report.pdl} />
                  {report.address?.street && (
                    <>
                      <Separator />
                      <DetailRow label="Adresse complète" value={report.address.street} />
                    </>
                  )}
                  {report.address?.postal_code && (
                    <>
                      <Separator />
                      <DetailRow label="Code Postal" value={report.address.postal_code} />
                    </>
                  )}
                  {report.address?.city && (
                    <>
                      <Separator />
                      <DetailRow label="Ville" value={report.address.city} />
                    </>
                  )}
                  <Separator />
                  {report.ape_code && report.ape_code !== '0000Z' && (
                    <>
                      <DetailRow 
                        label="Code APE INSEE" 
                        value={`${report.ape_code} - ${report.ape_description}`} 
                      />
                      <Separator />
                    </>
                  )}

                  <DetailRow label="Valeur Ajoutée (VA)" value={`${report.added_value.toLocaleString('fr-FR')} €`} />
                  <Separator />
                  <DetailRow label="Période d'analyse" value={`${report.period_start} - ${report.period_end}`} />
                  <Separator />
                  <DetailRow label="Consommation totale" value={`${report.consumption_mwh} MWh`} />
                  <Separator />
                  <DetailRow label="Montant TICFE actuel" value={`${report.current_ticfe_amount.toLocaleString('fr-FR')} €`} />
                </CardContent>
              </Card>

              {/* Eligibility Criteria */}
              <Card>
                <CardHeader>
                  <CardTitle>Critères d'éligibilité au tarif réduit</CardTitle>
                  <CardDescription>
                    Les catégories d'entreprises éligibles selon la réglementation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {report.eligibility === 'Eligible' ? (
                    <p className="text-sm text-muted-foreground">
                      Votre entreprise entre dans la catégorie : <strong className="text-success">{report.eligibility_reason}</strong>
                    </p>
                  ) : report.eligibility === 'Non-Eligible' ? (
                    <p className="text-sm text-muted-foreground">
                      Votre entreprise n'est actuellement pas éligible. <strong className="text-destructive">Motif : {report.eligibility_reason}</strong>
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Votre entreprise entre dans la catégorie : <strong className="text-success">{report.eligibility_reason}</strong>
                    </p>
                  )}
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Toutes les catégories éligibles :</h4>
                    <ul className="space-y-3">
                      <EligibilityItem 
                        text="Entreprises industrielles électro-intensives" 
                        active={report.eligibility_reason.includes('électro-intensive')} 
                        description="L'électricité représente une part importante des coûts de production"
                      />
                      <EligibilityItem 
                        text="Entreprises exposées à un risque de fuite de carbone" 
                        description="Secteurs identifiés comme exposés à une forte concurrence internationale"
                      />
                      <EligibilityItem 
                        text="Entreprises hyper électro-intensives" 
                        description="L'électricité représente ≥ 50% du coût de production"
                      />
                      <EligibilityItem 
                        text="Installations soumises au système d'échange de quotas d'émission (SEQE)" 
                        description="Entreprises participant au marché européen du carbone"
                      />
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Responsibilities */}
              <Card>
                <CardHeader>
                  <CardTitle>Responsabilités et engagements</CardTitle>
                  <CardDescription>
                    Répartition des rôles entre Place des Énergies et votre entreprise
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-primary/5 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Building className="w-5 h-5 text-primary" />
                      Place Des Énergies - Votre partenaire
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                      Place des Énergies agit en tant qu'<strong>intermédiaire indépendant</strong> pour :
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>Analyser votre éligibilité au tarif réduit de TICFE</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>Assister à la collecte et l'analyse de vos données de consommation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>Fournir les informations nécessaires pour vos demandes de tarif réduit</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>Accompagner la constitution de vos dossiers de remboursement</span>
                      </li>
                    </ul>
                    <Alert className="mt-4">
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription className="text-xs">
                        Place des Énergies ne peut être tenue responsable d'un rejet de dossier dû à des informations 
                        inexactes, incomplètes, ou non communiquées par le client, ou à des changements réglementaires.
                      </AlertDescription>
                    </Alert>
                  </div>
                  
                  <Separator />
                  
                  <div className="bg-accent/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-success" />
                      Votre entreprise - Responsabilités
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                      En tant que client, vous êtes responsable de :
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">•</span>
                        <span><strong>Valider l'exactitude</strong> de toutes les informations transmises à Place des Énergies</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">•</span>
                        <span>Faire <strong>vérifier les dossiers</strong> par votre expert-comptable ou conseiller fiscal</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">•</span>
                        <span>Conserver et pouvoir fournir <strong>tous les documents justificatifs</strong> en cas de contrôle</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-success mt-0.5">•</span>
                        <span>Répondre aux éventuelles demandes de l'administration fiscale</span>
                      </li>
                    </ul>
                    <Alert className="mt-4">
                      <Info className="w-4 h-4" />
                      <AlertDescription className="text-xs">
                        L'administration fiscale peut vérifier votre éligibilité à tout moment. Il est essentiel de 
                        conserver tous les documents justificatifs (factures, déclarations, audits énergétiques, etc.).
                      </AlertDescription>
                    </Alert>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-semibold mb-2 text-sm">Notre engagement qualité</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Place des Énergies s'engage à agir avec <strong>diligence et professionnalisme</strong> dans les 
                      limites des informations fournies. Nous mettons tout en œuvre pour optimiser vos chances d'obtenir 
                      le tarif réduit et maximiser vos économies dans le respect de la réglementation en vigueur.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card className="border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Nous contacter
                  </CardTitle>
                  <CardDescription>
                    Votre conseiller Place des Énergies est à votre disposition
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3">Votre conseiller</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>{report.contact?.name || 'Séverine MONNIER'}</strong></p>
                        <p className="text-muted-foreground">{report.contact?.title || 'Chargée d\'affaires'}</p>
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-muted-foreground">📞</span>
                          <a href={`tel:${(report.contact?.phone || '0659455322').replace(/\s/g, '')}`} className="text-primary hover:underline">
                            {report.contact?.phone || '06 59 45 53 22'}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">✉️</span>
                          <a href={`mailto:${report.contact?.email || 's.monnier@placedesenergies.com'}`} className="text-primary hover:underline">
                            {report.contact?.email || 's.monnier@placedesenergies.com'}
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-3">Place des Énergies</h4>
                      <div className="space-y-2 text-sm">
                        {report.agency?.name && (
                          <p className="text-muted-foreground">{report.agency.name}</p>
                        )}
                        <div className="flex items-start gap-2 mt-3">
                          <span className="text-muted-foreground mt-0.5">📍</span>
                          <div>
                            {report.agency?.address ? (
                              <>
                                <p>{report.agency.address}</p>
                                {report.agency.city && <p>{report.agency.city}</p>}
                              </>
                            ) : (
                              <p className="text-muted-foreground italic">Agence non renseignée</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Contact Info Modal */}
      <ContactModal
        open={showContactModal}
        onOpenChange={setShowContactModal}
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        onSave={handleSaveContact}
        saving={savingContact}
      />
    </div>
  );
};

const SavingsCard = ({ title, amount, description, icon }) => {
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center text-primary">
            {icon}
          </div>
        </div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-3xl font-bold text-primary mb-2">
          {amount.toLocaleString('fr-FR')} €
        </p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
};

const DetailRow = ({ label, value }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
};

const EligibilityItem = ({ text, active = false, description = null }) => {
  return (
    <li className="flex items-start gap-3">
      {active ? (
        <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1">
        <span className={active ? "font-medium" : "text-muted-foreground"}>{text}</span>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>
    </li>
  );
};

export default ReportViewPage;
